import React, { useEffect, useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { userRequest } from "../requestMethod";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addAddress } from "../redux/cartRedux";
import jwt_decode from "jwt-decode";

const Input = styled.input`
  display: block;
  padding: 0;
  height: 35px;

  line-height: 1.15;
  color: #30313d;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.15;
  margin-bottom: 1em;
  background-color: #fff;
  border-radius: 5px;
  transition: background 0.15s ease, border 0.15s ease, box-shadow 0.15s ease,
    color 0.15s ease;
  border: 1px solid #e6e6e6;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
  padding: 0.3em 0.6em;
`;

const Signin = styled(Link)`
  background: #5469d4;
  font-family: Roboto, Arial, sans-serif;
  text-align: center;
  color: #ffffff;
  border-radius: 4px;
  border: 0;
  padding: 12px 5px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: block;
  transition: all 0.2s ease;
  box-shadow: 0px 4px 5.5px 0px rgba(0, 0, 0, 0.07);
  width: 100%;
  text-decoration: none;
  &:hover {
    background: #485fcf;
  }
`;

export default function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const user = useSelector((state) => state.user.currentUser);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [billingAddress, setBillingAddress] = useState({});
  const [newBillingAddress, setNewBillingAddress] = useState({});
  const [newUser, setNewUser] = useState({});

  const dispatch = useDispatch();

  var accessPermitted = null;
  var tokenNotExpired = null;
  if (user?.accessToken) {
    var decoded = jwt_decode(user.accessToken);
    const tokenDate = new Date(decoded.exp * 1000);
    tokenNotExpired = !(new Date() - tokenDate > 0);
    accessPermitted = user && tokenNotExpired;
  } else {
    accessPermitted = false;
  }

  useEffect(async () => {
    if (!stripe) {
      return;
    }
    const getUserAddress = async () => {
      try {
        const res = await userRequest(user.accessToken).get(
          `users/billing/${user._id}?userId=${user._id}`
        );

        setBillingAddress(res.data);
      } catch (err) {}
    };
    getUserAddress();

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const handleChange = (e) => {
    setNewBillingAddress((prev) => {
      return { ...prev, [e.target.name]: e.target.value };
    });
    setNewUser((prev) => {
      return { ...prev, [e.target.name]: e.target.value };
    });

    if (newUser.firsname && newUser.lastname) {
      setNewUser((prev) => {
        return {
          ...prev,
          [newUser.username]:
            newUser.firstname +
            newUser.lastname +
            Math.floor(Math.random() * (9999 - 1000) + 1000),
        };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (user !== null) {
      if (
        JSON.stringify(Object.keys(billingAddress)) === "{}" ||
        billingAddress?.address1 === null
      ) {
        const updateUserAddress = async () => {
          try {
            newBillingAddress.userId = user._id;
            const res = await userRequest(user.accessToken).put(
              `users/updateaddress/${user._id}`,
              { address: newBillingAddress }
            );

            setNewBillingAddress(res.data);
            dispatch(addAddress(newBillingAddress));
          } catch (err) {}
        };
        updateUserAddress();
      } else {
        try {
          dispatch(addAddress(billingAddress));
        } catch (err) {}
      }
    }

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: `${process.env.REACT_APP_BASE_URL}/success`,
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occurred.");
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "tabs",
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      {!accessPermitted ? (
        <>
          <h2>Existing Customers</h2>
          <br />

          <Signin to="/login?redirect=Cart">Sign in</Signin>

          <br />
          <hr />
          <br />
          <h2>New Customer?</h2>
          <Signin to="/register?redirect=Cart">Register</Signin>
          <br />
        </>
      ) : Object.keys(billingAddress).length === 0 ||
        billingAddress.postcode === null ||
        billingAddress.postcode === undefined ? (
        <>
          <h3 style={{ marginBottom: "1em" }}>Address</h3>
          <Input
            onChange={handleChange}
            name="address1"
            type="text"
            placeholder="Address Line 1"
            required
          />
          <Input
            onChange={handleChange}
            name="address2"
            type="text"
            placeholder="Address line 2"
          />
          <Input
            onChange={handleChange}
            type="text"
            name="city"
            placeholder="Town or City"
            required
          />
          <Input
            onChange={handleChange}
            type="text"
            name="postcode"
            placeholder="Postcode"
            required
          />
          <h3 style={{ marginBottom: "1em" }}>Payment details</h3>
          <PaymentElement
            id="payment-element"
            options={paymentElementOptions}
          />
          <button disabled={isLoading || !stripe || !elements} id="submit">
            <span id="button-text">
              {isLoading ? (
                <div className="spinner" id="spinner"></div>
              ) : (
                "Pay now"
              )}
            </span>
          </button>
          {/* Show any error or success messages */}
          {message && <div id="payment-message">{message}</div>}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: "0.5em",
            }}
          >
            <p style={{ fontSize: "1em", color: "#272727" }}>
              Payment secured by
            </p>
            <img
              src="https://www.rentoodle.com/img/strp-logo.svg"
              height="25px"
            />
          </div>
        </>
      ) : (
        <>
          <div>
            <h4>Address</h4>
            <p>{billingAddress?.address1}</p>
            <p>{billingAddress?.address2}</p>
            <p>{billingAddress?.city}</p>
            <p>{billingAddress?.postcode}</p>
            <br />
          </div>
          <h3 style={{ marginBottom: "1em" }}>Payment details</h3>
          <PaymentElement
            id="payment-element"
            options={paymentElementOptions}
          />
          <button disabled={isLoading || !stripe || !elements} id="submit">
            <span id="button-text">
              {isLoading ? (
                <div className="spinner" id="spinner"></div>
              ) : (
                "Pay now"
              )}
            </span>
          </button>
          {/* Show any error or success messages */}
          {message && <div id="payment-message">{message}</div>}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: "0.5em",
            }}
          >
            <p style={{ fontSize: "1em", color: "#272727" }}>
              Payment secured by
            </p>
            <img
              src="https://www.rentoodle.com/img/strp-logo.svg"
              height="25px"
            />
          </div>
        </>
      )}
    </form>
  );
}
